import type { LensCard, LensCardAlignment, LensCardTitleSize, LensTextScale } from '~/types/leLens';

/** Values exposed by the batch toolbar. `default` removes an explicit override. */
export type BulkCardPresentation = {
  titleAlignment?: 'default' | LensCardAlignment;
  titleSize?: 'default' | LensCardTitleSize;
  textScale?: 'default' | LensTextScale;
};

function supportsChartText(card: LensCard): boolean {
  return !['metric', 'table', 'insight'].includes(card.type);
}

/**
 * Applies a reader-facing presentation choice to an explicit selection only.
 *
 * It never touches data, query, layout, title copy, or cards outside the
 * selection. That makes multi-card formatting safe to use on a partly
 * arranged Lens: choosing centre alignment cannot move a neighbouring card.
 */
export function applyBulkCardPresentation(
  cards: ReadonlyArray<LensCard>,
  selectedIds: ReadonlyArray<string>,
  presentation: BulkCardPresentation,
): Array<LensCard> {
  const selected = new Set(selectedIds);
  if (selected.size === 0) {
    return [...cards];
  }

  return cards.map((card) => {
    if (!selected.has(card.id)) {
      return card;
    }

    const config = { ...(card.config ?? {}) };
    if (presentation.titleAlignment !== undefined) {
      if (presentation.titleAlignment === 'default') {
        delete config.titleAlignment;
      } else {
        config.titleAlignment = presentation.titleAlignment;
      }
    }
    if (presentation.titleSize !== undefined) {
      if (presentation.titleSize === 'default') {
        delete config.titleSize;
      } else {
        config.titleSize = presentation.titleSize;
      }
    }
    // Chart text is meaningful only on charts. Avoid writing inert config to
    // a metric, table or prose insight just because it shared a selection.
    if (presentation.textScale !== undefined && supportsChartText(card)) {
      if (presentation.textScale === 'default') {
        delete config.textScale;
      } else {
        config.textScale = presentation.textScale;
      }
    }
    return { ...card, config: Object.keys(config).length ? config : undefined };
  });
}
